import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef } from 'react';
import {
  getOrgPreferenceKey,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { ViewSummaryModel } from '../../../models/view.model';
import { fetchViews } from '../../../services/queries/MaggieViewQueries';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { PreferencesServiceV3 } from '../../../util/preferences-service-v3';
import { visibleViews } from '../state/roundsByFundView';
import { isFirstRenderDonePFJ } from '../state/UIStateJ';
import { selectedViewIdPFJ, viewsPFJ } from '../state/ViewStateJ';

/**
 * Syncs the given viewId into state (`selectedViewIdPFJ`).
 * Only sets state when the viewId is valid and exists in the user's available views.
 * A valid id the list does not have was created since the list loaded (by the agent, in
 * another tab, or by a colleague sharing a link), so the list is fetched again once per id;
 * an id still missing after that is a no-op here, and PortfolioOverview's render guards
 * handle it (Forbidden / Not Found).
 * Also suspends on `viewsPFJ`, ensuring views are loaded before the component renders.
 */
export function useSyncSelectedView(viewId: number) {
  const [views, setViews] = useAtom(viewsPFJ);
  const [selectedViewId, setSelectedViewId] = useAtom(selectedViewIdPFJ);
  const setIsFirstRenderDone = useSetAtom(isFirstRenderDonePFJ);
  const refetchedForViewIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const viewExists = views.some((view) => view.id === viewId);
    if (!viewExists) {
      if (Number.isInteger(viewId) && viewId > 0 && refetchedForViewIdRef.current !== viewId) {
        refetchedForViewIdRef.current = viewId;
        // The list stays in place meanwhile, so nothing suspends; a failed refetch leaves it as it
        // is, and the page says the view was not found.
        void fetchViews().then(setViews, () => undefined);
      }
      return;
    }

    if (viewId !== selectedViewId) {
      setIsFirstRenderDone(false);
      setSelectedViewId(viewId);
    }
  }, [viewId, selectedViewId, setIsFirstRenderDone, setSelectedViewId, setViews, views]);
}

export function getViewOrDefault<T extends ViewSummaryModel>(
  viewId: number | undefined,
  views: T[]
): T | undefined {
  if (views.length === 0) {
    return undefined;
  }

  if (viewId != null && !Number.isNaN(viewId)) {
    const view = views.find((view) => view.id === viewId);
    if (view) {
      return view;
    }
  }

  return views[0];
}

export function useDefaultPortfolioView() {
  const defaultViewPref = PreferencesServiceV3.get().getPreference<number>(
    getOrgPreferenceKey(PreferenceKey.portfolioLandingView, null),
    PreferenceScope.ORGANIZATION
  );
  const views = useAtomValue(viewsPFJ);
  const { showRoundsByFundView } = useFlags<MaggieFeatureFlags>();
  // A landing view hidden by its flag falls back to the first view the user can open.
  return getViewOrDefault(defaultViewPref, visibleViews(views, showRoundsByFundView));
}
